import React, { useState, forwardRef } from 'react';
import { Eye, EyeOff, AlertTriangle, ChevronDown } from 'lucide-react';
import { cn } from '@utils/cn';
import { useTema, FS, PASO, MONO, type CasaTema } from '@components/M02/casaTema';

/**
 * I-244 — los campos son los del DISEÑO GANADOR, en UNA sola forma.
 *
 *   · Etiqueta 12.5/600 en `fg` con asterisco `danger` (apagado: `fg3`) — `design_handoff_unidades_cotizador 2/casa-piezas-base.jsx:26-31` (`Campo`)
 *     y `handoff_claude_ingresos_vestido/casa-ingresos-captura.jsx:7-9`.
 *   · Caja: relleno 11/13 (10/13 con ícono), radio 10, borde `inputLine`, fondo `input` — `casa-ingresos-captura.jsx:15` (12-sep, más nuevo que
 *     `casa-piezas-base.jsx:38` 10/12) y `:62,:84` (10/13 con ícono).
 *   · Error = borde `dangerLine` + triángulo + texto 11.5 `danger`; la pista (11.5 `fg3`) SE ESCONDE cuando hay error — `casa-piezas-base.jsx:33-35`.
 *   · Apagado: fondo `chip`, texto `fg3` — `casa-piezas-base.jsx:40`. Ojito en la contraseña — `:59-64`.
 *   · Letra del texto del campo: el diseño escribe 14 (`captura.jsx:15`, `base.jsx:41`); 14 está a igual distancia de 13 y 15 ⇒ sube a 15 (D-2026-09-30-F) = `PASO.s15`.
 *     El área de texto escribe 13 (`base.jsx:92`) = `FS.campo`.
 *
 * ⚠️ `className` cae en la CAJA (lo visible; antes caía en el control): los anchos (`w-24`), `opacity-60`, `text-right` y `font-mono` (la caja NO fija la familia: el control la hereda de ella) siguen sirviendo.
 *    En cambio la LETRA (`text-xs`…) NO la gobierna una clase: el tamaño va en línea (`PASO.s15`, `FS.campo`) y le gana a toda clase `text-*`.
 *    Lo que NO puede seguir sirviendo es quitarle la caja con clases (`border-0 px-0 bg-transparent`: el relleno y el borde van en línea, y lo inline le gana a toda clase):
 *    para un control SIN caja (el compositor del chat, una celda que ya vive dentro de su propia caja) existe `sinCaja`.
 *
 * 🔴 UNA forma: `T = tema ?? useTema()` (sin proveedor `useTema()` devuelve el claro). Se apagó la rama «clásica» de clases Tailwind de color:
 *    ningún campo pinta ya `bg-white`/`border-rm-border`/`border-red-300`. Ningún hex ni `fontSize` numérico dentro de la pieza.
 */

/**
 * El `tema` de un campo ES el tema de la casa. Antes era un contrato de 9 colores (`label`/`bg`/`line`/`hint`…) con su propio puente; el diseño
 * necesita 15 (`chip`, `accent`, `dangerLine`…), así que el puente se volvió el mismo objeto. El nombre queda por los usuarios (`import type { CampoTema }`).
 */
export type CampoTema = CasaTema;

export interface OpcionesDeCaja {
  error?: boolean;
  apagado?: boolean;
  /** Con ícono o prefijo adelante el relleno vertical baja a 10 (`captura.jsx:62,:84`). */
  icono?: boolean;
  /** El campo tiene el foco. El diseño no dibuja el foco: se conserva el acento que ya tenía el Combobox abierto (va a GAPS). */
  enfocado?: boolean;
}

/** La caja del diseño (`cajaInput` / `inputBase`): la usan el campo de texto, el select, el dinero, el buscador y el área de texto. */
export function estiloDeCaja(T: CasaTema, { error, apagado, icono, enfocado }: OpcionesDeCaja = {}): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 9, boxSizing: 'border-box',
    padding: icono ? '10px 13px' : '11px 13px', borderRadius: 10,
    borderWidth: 1, borderStyle: 'solid',
    borderColor: error ? T.dangerLine : enfocado ? T.accent : T.inputLine,
    backgroundColor: apagado ? T.chip : T.input,
    color: apagado ? T.fg3 : T.fg,
    fontSize: PASO.s15,
    boxShadow: enfocado && !error ? `0 0 0 3px ${T.accentSoft}` : undefined,
    colorScheme: T.dark ? 'dark' : 'light',
  };
}

/** El control pelón que vive dentro de la caja (`inputPelon`). */
export function estiloPelon(T: CasaTema): React.CSSProperties {
  return {
    flex: 1, minWidth: 0, border: 0, backgroundColor: 'transparent', outline: 'none', padding: 0, margin: 0,
    color: 'inherit', fontSize: PASO.s15, fontFamily: 'inherit', textAlign: 'inherit', letterSpacing: 'inherit', colorScheme: T.dark ? 'dark' : 'light',
  };
}

interface EnvolturaProps {
  T: CasaTema;
  label?: string;
  /** El `id` del control: sin él la etiqueta no queda amarrada al campo (lectores de pantalla, `getByLabelText`). */
  htmlFor?: string;
  required?: boolean;
  apagado?: boolean;
  error?: string;
  hint?: string;
  /** Lo que va entre el campo y la pista/el error (el contador del área de texto). */
  entre?: React.ReactNode;
  children: React.ReactNode;
}

/** La envoltura del diseño (`Campo`): etiqueta arriba, pista o error abajo — la pista NO se encima al error. */
export const EnvolturaDeCampo: React.FC<EnvolturaProps> = ({ T, label, htmlFor, required, apagado, error, hint, entre, children }) => (
  <div>
    {label && (
      <label htmlFor={htmlFor} style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
        <span style={{ fontSize: FS.etiquetaCampo, fontWeight: 600, color: apagado ? T.fg3 : T.fg }}>{label}</span>
        {required && <span style={{ fontSize: FS.etiquetaCampo, color: T.danger }}>*</span>}
      </label>
    )}
    {children}
    {entre}
    {error ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: PASO.s11_5, color: T.danger }}>
        <AlertTriangle size={11} color={T.danger} aria-hidden />{error}
      </div>
    ) : hint ? (
      <div style={{ marginTop: 6, fontSize: PASO.s11_5, color: T.fg3 }}>{hint}</div>
    ) : null}
  </div>
);

// ─── Input ────────────────────────────────────────────────────
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  required?: boolean;
  /** Sólo el control, sin caja, etiqueta ni mensajes: para quien ya lo mete dentro de su propia caja (el compositor del chat). */
  sinCaja?: boolean;
  /** Sin esto el campo toma el tema de la casa (`useTema()`, el claro sin proveedor). Con esto, el que se le pase. */
  tema?: CampoTema;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, required, className, type = 'text', tema, sinCaja, disabled, style, onFocus, onBlur, ...props }, ref) => {
    const Tctx = useTema();
    const T = tema ?? Tctx;
    const [showPassword, setShowPassword] = useState(false);
    const [enfocado, setEnfocado] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    if (sinCaja) {
      return (
        <input
          ref={ref} type={inputType} disabled={disabled} className={cn(className)}
          style={{ ...estiloPelon(T), width: '100%', ...style }}
          onFocus={onFocus} onBlur={onBlur} {...props}
        />
      );
    }

    return (
      <EnvolturaDeCampo T={T} label={label} htmlFor={props.id} required={required} apagado={disabled} error={error} hint={hint}>
        <div className={cn(className)} style={{ ...estiloDeCaja(T, { error: !!error, apagado: disabled, icono: !!icon, enfocado }), cursor: disabled ? 'not-allowed' : 'text' }}>
          {icon && <span style={{ display: 'flex', flexShrink: 0, color: T.fg3 }}>{icon}</span>}
          <input
            ref={ref}
            type={inputType}
            disabled={disabled}
            style={{ ...estiloPelon(T), ...style }}
            onFocus={(e) => { setEnfocado(true); onFocus?.(e); }}
            onBlur={(e) => { setEnfocado(false); onBlur?.(e); }}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              title={showPassword ? 'Ocultar' : 'Ver'}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
              style={{ border: 0, backgroundColor: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', color: T.fg3 }}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
        </div>
      </EnvolturaDeCampo>
    );
  },
);
Input.displayName = 'Input';

// ─── Select ───────────────────────────────────────────────────
interface SelectOption {
  value: string | number;
  label: string;
  /** Una opción que se ve pero no se elige: sirve de separador («— Otros tipos —»). */
  disabled?: boolean;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  /** Sólo el control, sin caja, etiqueta, chevron ni mensajes: para quien ya lo mete dentro de su propia caja. */
  sinCaja?: boolean;
  /** Sin esto el campo toma el tema de la casa (`useTema()`, el claro sin proveedor). Con esto, el que se le pase. */
  tema?: CampoTema;
}

/** El select del diseño (`casa-ingresos-captura.jsx:62-70`): caja con el control nativo pelón (`appearance: none`) y el chevron `fg3` de 13. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, required, className, tema, sinCaja, disabled, style, onFocus, onBlur, ...props }, ref) => {
    const Tctx = useTema();
    const T = tema ?? Tctx;
    const [enfocado, setEnfocado] = useState(false);
    const opcionesDelSelect = (
      <>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </>
    );
    if (sinCaja) {
      return (
        <select
          ref={ref} disabled={disabled} className={cn(className)}
          style={{ ...estiloPelon(T), width: '100%', cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
          onFocus={onFocus} onBlur={onBlur} {...props}
        >
          {opcionesDelSelect}
        </select>
      );
    }
    return (
      <EnvolturaDeCampo T={T} label={label} htmlFor={props.id} required={required} apagado={disabled} error={error} hint={hint}>
        <div className={cn(className)} style={{ ...estiloDeCaja(T, { error: !!error, apagado: disabled, enfocado }), cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <select
            ref={ref}
            disabled={disabled}
            style={{ ...estiloPelon(T), appearance: 'none', WebkitAppearance: 'none', cursor: 'inherit', ...style }}
            onFocus={(e) => { setEnfocado(true); onFocus?.(e); }}
            onBlur={(e) => { setEnfocado(false); onBlur?.(e); }}
            {...props}
          >
            {opcionesDelSelect}
          </select>
          <ChevronDown size={13} color={T.fg3} aria-hidden style={{ flexShrink: 0 }} />
        </div>
      </EnvolturaDeCampo>
    );
  },
);
Select.displayName = 'Select';

// ─── Textarea ─────────────────────────────────────────────────
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Mínimo de caracteres: pinta el contador «Faltan N caracteres — pide mínimo N.» / «Listo, alcanza.» y `n/N` (`casa-piezas-base.jsx:85-99`). */
  min?: number;
  /** Sólo el control, sin caja, etiqueta ni mensajes: para quien ya lo mete dentro de su propia caja (el compositor del chat). */
  sinCaja?: boolean;
  /** Sin esto el campo toma el tema de la casa (`useTema()`, el claro sin proveedor). Con esto, el que se le pase. */
  tema?: CampoTema;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, required, min, className, tema, sinCaja, disabled, style, rows = 3, onChange, onFocus, onBlur, ...props }, ref) => {
    const Tctx = useTema();
    const T = tema ?? Tctx;
    const [enfocado, setEnfocado] = useState(false);
    // El contador mide lo que hay escrito: si el campo es controlado, el `value`; si no, lo que el propio campo lleva.
    const [interno, setInterno] = useState(String(props.defaultValue ?? ''));
    const texto = props.value !== undefined ? String(props.value ?? '') : interno;
    const largo = texto.trim().length;
    const falta = min ? min - largo : 0;
    const contador = min ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: PASO.s11_5, color: falta > 0 ? T.danger : T.fg3 }}>
          {falta > 0 ? `Faltan ${falta} caracteres — pide mínimo ${min}.` : 'Listo, alcanza.'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: PASO.s11, fontFamily: MONO, color: T.fg3 }}>{largo}/{min}</span>
      </div>
    ) : null;
    const hayError = !!error || (falta > 0 && texto.length > 0);

    if (sinCaja) {
      return (
        <textarea
          ref={ref} rows={rows} disabled={disabled} className={cn(className)}
          style={{ ...estiloPelon(T), display: 'block', width: '100%', lineHeight: 1.45, fontSize: FS.campo, resize: 'vertical', ...style }}
          onChange={onChange} onFocus={onFocus} onBlur={onBlur} {...props}
        />
      );
    }

    return (
      <EnvolturaDeCampo T={T} label={label} htmlFor={props.id} required={required} apagado={disabled} error={error} hint={hint} entre={contador}>
        <textarea
          ref={ref}
          rows={rows}
          disabled={disabled}
          className={cn(className)}
          style={{
            ...estiloDeCaja(T, { error: hayError, apagado: disabled, enfocado }),
            display: 'block', width: '100%', resize: 'vertical', lineHeight: 1.45, fontSize: FS.campo, outline: 'none',
            ...style,
          }}
          onChange={(e) => { setInterno(e.target.value); onChange?.(e); }}
          onFocus={(e) => { setEnfocado(true); onFocus?.(e); }}
          onBlur={(e) => { setEnfocado(false); onBlur?.(e); }}
          {...props}
        />
      </EnvolturaDeCampo>
    );
  },
);
Textarea.displayName = 'Textarea';

export default Input;
